import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { intl, DmsModal, DmsButton } from "dmsbasereact";
import {
    Typography,
    Chip,
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";
import { DmsRichTextViewer } from "../../components/DmsRichTextEditor";
import { downloadFile } from "../../utils/fileDownload";
import { useFormatters } from "../../hooks/useFormater";
import { STATUS_COLORS } from "./constants";

export const AnnotationViewModal = ({ open, onClose, annotation, statusLabels, sequentialNumber }) => {
    const { formatDateTime } = useFormatters();
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);

    const handlePreview = useCallback((af) => {
        setPreviewFile(af);
        setPreviewModalOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewModalOpen(false);
        setPreviewFile(null);
    }, []);

    const handleDownload = useCallback((af) => {
        const filePk = af.file_pk || af.file?.pk;
        const fileName = af.file?.original_filename;
        downloadFile(filePk, fileName);
    }, []);

    if (!annotation) return null;

    const statusValue = annotation.status?.value !== undefined
        ? annotation.status.value
        : annotation.status;

    const files = annotation.files || [];

    return (
        <>
            <DmsModal isVisible={open} onClose={onClose} maxWidth="md" fullWidth>
                <DmsModal.Header
                    title={intl.get("ViewAnnotation").d("View Annotation")}
                    onClose={onClose}
                />
                <DmsModal.Body>
                    <Box sx={{ p: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="h6">
                                #{sequentialNumber || annotation.pk} - {annotation.title}
                            </Typography>
                            <Chip
                                label={statusLabels[statusValue] || "-"}
                                size="small"
                                color={STATUS_COLORS[statusValue] || "default"}
                            />
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block">
                            <strong>{intl.get("LastUpdate").d("Last Update")}:</strong> {formatDateTime(annotation.updated_on)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                            <strong>{intl.get("UpdatedBy").d("Updated by")}:</strong> {annotation.updated_by?.user_name || "-"}
                        </Typography>

                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {intl.get("Annotation").d("Annotation")}
                        </Typography>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 2,
                                mb: 3,
                                bgcolor: "grey.50",
                            }}
                        >
                            <DmsRichTextViewer html={annotation.text} />
                        </Box>

                        {files.length > 0 && (
                            <>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {intl.get("AttachedFiles").d("Attached Files")}
                                </Typography>
                                <List dense disablePadding>
                                    {files.map((af) => {
                                        const filePk = af.file_pk || af.file?.pk;
                                        const fileName = af.file?.original_filename || `File #${af.file_pk}`;

                                        return (
                                            <ListItem
                                                key={af.pk || filePk}
                                                sx={{
                                                    bgcolor: "background.paper",
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                                secondaryAction={
                                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                                        <Tooltip title={intl.get("Preview").d("Preview")}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handlePreview(af)}
                                                                disabled={!filePk}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={intl.get("Download").d("Download")}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDownload(af)}
                                                                disabled={!filePk}
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            >
                                                <ListItemIcon>
                                                    <DescriptionIcon color="action" />
                                                </ListItemIcon>
                                                <ListItemText primary={fileName} />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </>
                        )}
                    </Box>
                </DmsModal.Body>
                <DmsModal.Footer>
                    <DmsButton
                        onClick={onClose}
                        text={intl.get("Close").d("Close")}
                    />
                </DmsModal.Footer>
            </DmsModal>

            <DmsFileViewerModal
                isOpen={previewModalOpen}
                onClose={handleClosePreview}
                filePk={previewFile?.file_pk || previewFile?.file?.pk}
                fileName={previewFile?.file?.original_filename}
                title={previewFile?.file?.original_filename}
            />
        </>
    );
};

AnnotationViewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    annotation: PropTypes.object,
    statusLabels: PropTypes.object.isRequired,
    sequentialNumber: PropTypes.number,
};
